import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axiosInstance from '../api/axiosInstance';
import { extractErrorMessage } from '../api/errorUtils';
import { showNotification } from './notificationSlice';

const BASE = 'techsavanna_pos.api.purchase_requisition';

const extractMsg = (response) => {
  if (response.data?.message && typeof response.data.message === 'object') {
    return response.data.message;
  }
  return response.data?.message || response.data;
};

export const listRestaurantPOs = createAsyncThunk(
  'restaurantPR/listRestaurantPOs',
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE}.list_restaurant_purchase_orders`, { params });
      const data = extractMsg(response);
      return {
        requisitions: data?.purchase_orders || data?.data || [],
        pagination: {
          page: data?.meta?.page || params.page || 1,
          page_size: data?.meta?.page_size || params.page_size || 20,
          total: data?.meta?.total || data?.total_count || 0,
          total_pages: data?.meta?.total_pages || 0,
        },
      };
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const fetchIngredientItems = createAsyncThunk(
  'restaurantPR/fetchIngredientItems',
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE}.list_ingredient_items`, { params });
      const data = extractMsg(response);
      return data?.items || data?.data || data?.factors || [];
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const createRestaurantPO = createAsyncThunk(
  'restaurantPR/createRestaurantPO',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE}.create_restaurant_purchase_order`, payload);
      const data = extractMsg(response);
      dispatch(showNotification({ message: 'Purchase requisition raised successfully', severity: 'success', title: 'Success' }));
      return data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const acceptRestaurantPO = createAsyncThunk(
  'restaurantPR/acceptRestaurantPO',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE}.accept_restaurant_po`, payload);
      const data = extractMsg(response);
      dispatch(showNotification({ message: 'Purchase order accepted successfully', severity: 'success', title: 'Success' }));
      return data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const dispatchRestaurantPO = createAsyncThunk(
  'restaurantPR/dispatchRestaurantPO',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${BASE}.dispatch_restaurant_po`, payload);
      const data = extractMsg(response);
      dispatch(showNotification({ message: 'Order dispatched to restaurant', severity: 'success', title: 'Dispatched' }));
      return data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const listDispatchedOrders = createAsyncThunk(
  'restaurantPR/listDispatchedOrders',
  async (params = {}, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE}.list_dispatched_orders`, { params });
      const data = extractMsg(response);
      return {
        dispatched: data?.purchase_orders || data?.data || [],
        pagination: {
          page: data?.meta?.page || params.page || 1,
          page_size: data?.meta?.page_size || params.page_size || 20,
          total: data?.meta?.total || data?.total_count || 0,
          total_pages: data?.meta?.total_pages || 0,
        },
      };
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

export const getRestaurantPO = createAsyncThunk(
  'restaurantPR/getRestaurantPO',
  async (params, { dispatch, rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${BASE}.get_restaurant_po`, { params });
      const data = extractMsg(response);
      return data?.purchase_order || data;
    } catch (error) {
      const msg = extractErrorMessage(error);
      dispatch(showNotification({ message: msg, severity: 'error', title: 'Error' }));
      return rejectWithValue(msg);
    }
  }
);

const initialState = {
  requisitions: [],
  dispatched: [],
  selectedPO: null,
  pagination: { page: 1, page_size: 20, total: 0, total_pages: 0 },
  dispatchedPagination: { page: 1, page_size: 20, total: 0, total_pages: 0 },
  filters: {},
  isLoading: false,
  isLoadingIngredients: false,
  isSaving: false,
  error: null,
  ingredientItems: [],
};

const restaurantPRSlice = createSlice({
  name: 'restaurantPR',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
      state.pagination.page = 1;
    },
    resetFilters: (state) => {
      state.filters = {};
      state.pagination.page = 1;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pagination.page_size = action.payload;
      state.pagination.page = 1;
    },
    clearSelectedPO: (state) => {
      state.selectedPO = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(listRestaurantPOs.pending, (state) => { state.isLoading = true; state.error = null; })
      .addCase(listRestaurantPOs.fulfilled, (state, action) => {
        state.isLoading = false;
        state.requisitions = action.payload.requisitions;
        state.pagination = { ...state.pagination, ...action.payload.pagination };
      })
      .addCase(listRestaurantPOs.rejected, (state, action) => { state.isLoading = false; state.error = action.payload; })

      .addCase(fetchIngredientItems.pending, (state) => { state.isLoadingIngredients = true; })
      .addCase(fetchIngredientItems.fulfilled, (state, action) => {
        state.isLoadingIngredients = false;
        state.ingredientItems = action.payload;
      })
      .addCase(fetchIngredientItems.rejected, (state) => { state.isLoadingIngredients = false; })

      .addCase(createRestaurantPO.pending, (state) => { state.isSaving = true; })
      .addCase(createRestaurantPO.fulfilled, (state) => { state.isSaving = false; })
      .addCase(createRestaurantPO.rejected, (state, action) => { state.isSaving = false; state.error = action.payload; })

      .addCase(acceptRestaurantPO.pending, (state) => { state.isSaving = true; })
      .addCase(acceptRestaurantPO.fulfilled, (state) => { state.isSaving = false; })
      .addCase(acceptRestaurantPO.rejected, (state) => { state.isSaving = false; })

      .addCase(dispatchRestaurantPO.pending, (state) => { state.isSaving = true; })
      .addCase(dispatchRestaurantPO.fulfilled, (state) => { state.isSaving = false; })
      .addCase(dispatchRestaurantPO.rejected, (state) => { state.isSaving = false; })

      .addCase(listDispatchedOrders.pending, (state) => { state.isLoading = true; })
      .addCase(listDispatchedOrders.fulfilled, (state, action) => {
        state.isLoading = false;
        state.dispatched = action.payload.dispatched;
        state.dispatchedPagination = { ...state.dispatchedPagination, ...action.payload.pagination };
      })
      .addCase(listDispatchedOrders.rejected, (state) => { state.isLoading = false; })

      .addCase(getRestaurantPO.fulfilled, (state, action) => { state.selectedPO = action.payload; });
  },
});

export const { setFilters, resetFilters, setPage, setPageSize, clearSelectedPO } = restaurantPRSlice.actions;
export default restaurantPRSlice.reducer;
